/**
 * Warms the browser's HTTP cache with the route data for `page` so a
 * subsequent navigation there serves from cache.
 *
 * This deliberately avoids <link rel="prefetch"> (PrefetchPageLinks): Safari
 * ignores the tag entirely, and Chromium stores link-prefetched responses as
 * Sec-Purpose-flagged cache entries that later navigations reuse
 * inconsistently. A plain fetch primes an ordinary HTTP-cache entry instead;
 * the X-Purpose header lets the server mark the response cacheable (see
 * handleDataRequest in entry.server). The entry only lives for that
 * response's short max-age, so warm close to the navigation, not on page
 * load.
 *
 * The warmed URL must byte-match React Router's single-fetch request, whose
 * `_routes` param lists the routes that revalidate — pass the ids of those
 * routes in match order. If the revalidation policy and the passed list ever
 * drift apart, navigations just stop matching the warmed URL — the cost is
 * an unused background request, never a wrong response.
 */
export const warmRouteData = (
    page: string,
    revalidatingRouteIds: string,
): Promise<void> => {
    const url = new URL(page, window.location.origin);
    url.pathname += '.data';
    url.searchParams.set('_routes', revalidatingRouteIds);
    return (
        fetch(url, { headers: { 'X-Purpose': 'prefetch' } })
            // Drain the body so the cache entry completes.
            .then((response) => response.arrayBuffer())
            .then(() => undefined)
            .catch(() => {
                // Cache warming only — the navigation fetches for real.
            })
    );
};
